import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Phone, MapPin, Clock, User, Car, Calendar,
  ChevronRight, Search, Filter, Users, DollarSign, FileText,
  CheckCircle, AlertCircle, Loader2, Star, Plus, X, Send,
  Truck, Navigation, Timer, ClipboardList
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- Sub-components ----

const StatCard = ({ icon: Icon, label, value, color = "text-black", bg = "bg-gray-50" }) => (
  <div className={`${bg} rounded-lg p-4`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`h-4 w-4 ${color}`} />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    unassigned: 'bg-yellow-100 text-yellow-700',
    assigned: 'bg-blue-100 text-blue-700',
    confirmed: 'bg-green-100 text-green-700',
    en_route: 'bg-purple-100 text-purple-700',
    in_service: 'bg-indigo-100 text-indigo-700',
    completed: 'bg-gray-100 text-gray-500',
    cancelled: 'bg-red-100 text-red-600',
    pending: 'bg-yellow-100 text-yellow-700',
    pending_payment: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${styles[status] || styles.confirmed}`}
      data-testid={`status-badge-${status}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const BookingRow = ({ booking, onAssign, onViewDetails }) => {
  const ci = booking.customerInfo || {};
  const d = booking.dispatch;
  const hasDriver = d && d.driver_name;

  return (
    <div className="flex items-center gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
      data-testid={`booking-row-${booking.bookingId}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-xs text-gray-400">{booking.bookingId}</span>
          <StatusBadge status={hasDriver ? 'assigned' : booking.status} />
        </div>
        <p className="font-semibold text-sm text-black truncate">
          {ci.firstName} {ci.lastName}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {booking.pickupTime || '—'}</span>
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {booking.pickupLocation?.slice(0, 40) || '—'}</span>
          {booking.serviceType && <span className="capitalize">{booking.serviceType.replace(/-/g, ' ')}</span>}
        </div>
      </div>

      {/* Driver Assignment */}
      <div className="text-right min-w-[140px]">
        {hasDriver ? (
          <div>
            <p className="text-sm font-semibold text-black">{d.driver_name}</p>
            <p className="text-xs text-gray-400">{d.driver_phone}</p>
            {d.auto_assigned && (
              <span className="text-[9px] bg-green-100 text-green-600 px-1.5 py-0.5 rounded font-semibold">AUTO</span>
            )}
          </div>
        ) : (
          <Button size="sm" onClick={() => onAssign(booking)} className="bg-blue-600 hover:bg-blue-700 text-xs"
            data-testid={`assign-btn-${booking.bookingId}`}>
            <User className="h-3 w-3 mr-1" /> Assign
          </Button>
        )}
      </div>

      <div className="text-right min-w-[60px]">
        <p className="font-bold text-black">${booking.totalPrice || 0}</p>
        <p className="text-[10px] text-gray-400">{booking.hours || 3}hrs</p>
      </div>

      <button onClick={() => onViewDetails(booking)} className="text-gray-400 hover:text-black" data-testid={`details-btn-${booking.bookingId}`}>
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );
};

// ---- Main Component ----
const DispatchBoard = () => {
  const navigate = useNavigate();
  const [board, setBoard] = useState({});
  const [stats, setStats] = useState({});
  const [drivers, setDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Assignment modal
  const [assigningBooking, setAssigningBooking] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Detail panel
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [dispatchNotes, setDispatchNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  // Customer CRM panel
  const [showCRM, setShowCRM] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');

  // Manifest
  const [showManifest, setShowManifest] = useState(false);
  const [manifest, setManifest] = useState(null);

  // Auth check
  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then(r => { if (!r.ok) navigate('/admin/login'); })
      .catch(() => navigate('/admin/login'));
  }, [navigate]);

  const loadBoard = useCallback(async () => {
    try {
      const [boardRes, statsRes] = await Promise.all([
        fetch(`${API}/api/dispatch/board`),
        fetch(`${API}/api/dispatch/stats`),
      ]);
      const boardData = await boardRes.json();
      const statsData = await statsRes.json();
      setBoard(boardData.board || {});
      setDrivers(boardData.available_drivers || []);
      setAllDrivers(boardData.all_drivers || []);
      setStats(statsData);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(loadBoard, 30000);
    return () => clearInterval(interval);
  }, [loadBoard]);

  const allBookings = Object.values(board).flat();
  const filteredBookings = activeTab === 'all'
    ? allBookings
    : board[activeTab] || [];

  const searchFiltered = searchTerm
    ? filteredBookings.filter(b => {
        const ci = b.customerInfo || {};
        const s = searchTerm.toLowerCase();
        return (ci.firstName + ' ' + ci.lastName).toLowerCase().includes(s)
          || b.bookingId?.toLowerCase().includes(s)
          || b.pickupLocation?.toLowerCase().includes(s)
          || (b.dispatch?.driver_name || '').toLowerCase().includes(s);
      })
    : filteredBookings;

  // ---- Assign driver ----
  const openAssignModal = async (booking) => {
    setAssigningBooking(booking);
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${API}/api/dispatch/suggest-drivers/${booking.bookingId}`);
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch { setSuggestions([]); }
    setLoadingSuggestions(false);
  };

  const confirmAssign = async (driverId) => {
    if (!assigningBooking) return;
    await fetch(`${API}/api/dispatch/assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_id: assigningBooking.bookingId, driver_id: driverId })
    });
    setAssigningBooking(null);
    loadBoard();
  };

  // ---- Auto-Assign ----
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/dispatch/auto-assign/settings`)
      .then(r => r.json())
      .then(d => setAutoAssignEnabled(d.enabled || false))
      .catch(() => {});
  }, []);

  const toggleAutoAssign = async () => {
    const newVal = !autoAssignEnabled;
    await fetch(`${API}/api/dispatch/auto-assign/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: newVal })
    });
    setAutoAssignEnabled(newVal);
  };

  const autoAssignAll = async () => {
    setAutoAssigning(true);
    try {
      const res = await fetch(`${API}/api/dispatch/auto-assign-all`, { method: 'POST' });
      const data = await res.json();
      alert(`Auto-assigned ${data.assigned} of ${data.total_processed} bookings`);
      loadBoard();
    } catch { }
    setAutoAssigning(false);
  };

  // ---- Detail Panel ----
  const openDetails = async (booking) => {
    setSelectedBooking(booking);
    try {
      const res = await fetch(`${API}/api/dispatch/notes/${booking.bookingId}`);
      const data = await res.json();
      setDispatchNotes(data.notes || []);
    } catch { setDispatchNotes([]); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedBooking) return;
    await fetch(`${API}/api/dispatch/notes/${selectedBooking.bookingId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: newNote, author: 'Dispatch' })
    });
    setNewNote('');
    const res = await fetch(`${API}/api/dispatch/notes/${selectedBooking.bookingId}`);
    const data = await res.json();
    setDispatchNotes(data.notes || []);
  };

  // ---- CRM ----
  const loadCustomers = async () => {
    setShowCRM(true);
    try {
      const res = await fetch(`${API}/api/dispatch/customers?search=${customerSearch}`);
      const data = await res.json();
      setCustomers(data.customers || []);
    } catch { setCustomers([]); }
  };

  const loadCustomerProfile = async (email) => {
    try {
      const res = await fetch(`${API}/api/dispatch/customers/${encodeURIComponent(email)}`);
      const data = await res.json();
      setSelectedCustomer(data);
    } catch { }
  };

  const toggleVIP = async (email, current) => {
    await fetch(`${API}/api/dispatch/customers/${encodeURIComponent(email)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_vip: !current })
    });
    loadCustomers();
  };

  // ---- Manifest ----
  const loadManifest = async () => {
    setShowManifest(true);
    try {
      const res = await fetch(`${API}/api/dispatch/manifest?date=${selectedDate}`);
      const data = await res.json();
      setManifest(data);
    } catch { setManifest(null); }
  };

  const tabs = [
    { key: 'all', label: 'All', count: allBookings.length },
    { key: 'unassigned', label: 'Unassigned', count: (board.unassigned || []).length },
    { key: 'assigned', label: 'Assigned', count: (board.assigned || []).length },
    { key: 'confirmed', label: 'Confirmed', count: (board.confirmed || []).length },
    { key: 'en_route', label: 'En Route', count: (board.en_route || []).length },
    { key: 'in_service', label: 'In Service', count: (board.in_service || []).length },
    { key: 'completed', label: 'Completed', count: (board.completed || []).length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dispatch-board">
      {/* Header */}
      <div className="bg-black text-white px-6 py-4 sticky top-0 z-30">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white" data-testid="back-to-admin">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">ACE Dispatch Center</h1>
              <p className="text-xs text-gray-400">Operations Command</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={loadManifest}
              className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
              data-testid="manifest-btn">
              <ClipboardList className="h-4 w-4 mr-1" /> Run Sheet
            </Button>
            <Button size="sm" variant="outline" onClick={loadCustomers}
              className="border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
              data-testid="crm-btn">
              <Users className="h-4 w-4 mr-1" /> CRM
            </Button>
            <Button size="sm" onClick={() => navigate('/admin/tracking')}
              className="bg-red-600 hover:bg-red-700"
              data-testid="live-tracking-btn">
              <Navigation className="h-4 w-4 mr-1" /> Live Track
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-6 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <StatCard icon={Calendar} label="Today" value={stats.today_rides || 0} color="text-blue-600" bg="bg-blue-50" />
          <StatCard icon={Timer} label="This Week" value={stats.week_rides || 0} color="text-indigo-600" bg="bg-indigo-50" />
          <StatCard icon={Car} label="This Month" value={stats.month_rides || 0} color="text-purple-600" bg="bg-purple-50" />
          <StatCard icon={DollarSign} label="Month Rev" value={`$${(stats.month_revenue || 0).toLocaleString()}`} color="text-green-600" bg="bg-green-50" />
          <StatCard icon={AlertCircle} label="Unassigned" value={stats.unassigned_bookings || 0} color="text-yellow-600" bg="bg-yellow-50" />
          <StatCard icon={User} label="Drivers Avail" value={stats.available_drivers || 0} color="text-cyan-600" bg="bg-cyan-50" />
          <StatCard icon={Users} label="Total Drivers" value={stats.total_active_drivers || 0} color="text-gray-600" bg="bg-gray-100" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, ID, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-white border-gray-200"
              data-testid="dispatch-search"
            />
          </div>
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-auto bg-white border-gray-200"
            data-testid="dispatch-date"
          />
          <Button size="sm" variant="outline" onClick={loadBoard} className="border-gray-200">
            Refresh
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={autoAssignEnabled}
                onChange={toggleAutoAssign}
                className="accent-red-600 h-4 w-4"
                data-testid="auto-assign-toggle"
              />
              <span className="text-gray-600">Auto-Assign</span>
            </label>
            <Button
              size="sm"
              onClick={autoAssignAll}
              disabled={autoAssigning}
              className="bg-green-600 hover:bg-green-700 text-xs"
              data-testid="auto-assign-all-btn"
            >
              {autoAssigning ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Truck className="h-3 w-3 mr-1" />}
              Assign All
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                activeTab === t.key
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label} {t.count > 0 && <span className="ml-1 opacity-70">({t.count})</span>}
            </button>
          ))}
        </div>

        {/* Bookings List */}
        <Card className="border-gray-200 overflow-hidden" data-testid="bookings-list">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500">{searchFiltered.length} bookings</span>
            <span className="text-xs text-gray-400">Auto-refreshes every 30s</span>
          </div>
          {searchFiltered.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <Car className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No bookings match your filter</p>
            </div>
          ) : (
            searchFiltered.map(b => (
              <BookingRow
                key={b.bookingId}
                booking={b}
                onAssign={openAssignModal}
                onViewDetails={openDetails}
              />
            ))
          )}
        </Card>
      </div>

      {/* ====== ASSIGN DRIVER MODAL ====== */}
      {assigningBooking && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAssigningBooking(null)}>
          <Card className="w-full max-w-lg p-6 bg-white" onClick={e => e.stopPropagation()} data-testid="assign-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-black">Assign Driver</h3>
              <button onClick={() => setAssigningBooking(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-1">
              Booking: <span className="font-mono text-black">{assigningBooking.bookingId}</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {assigningBooking.customerInfo?.firstName} {assigningBooking.customerInfo?.lastName} — {assigningBooking.pickupTime} — {assigningBooking.pickupLocation?.slice(0, 50)}
            </p>

            {loadingSuggestions ? (
              <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" /></div>
            ) : suggestions.length === 0 ? (
              <p className="py-6 text-center text-gray-400 text-sm">No available drivers. Add drivers in Driver Management.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {suggestions.map(d => (
                  <div key={d.driver_id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      d.recommended ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}
                    data-testid={`driver-suggestion-${d.driver_id}`}
                  >
                    <div>
                      <p className="font-semibold text-sm text-black flex items-center gap-2">
                        {d.name}
                        {d.recommended && <span className="text-[10px] bg-green-200 text-green-700 px-1.5 py-0.5 rounded">Recommended</span>}
                        {d.is_busy_same_day && <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">Busy same day</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {d.vehicle_assigned || 'No vehicle'} | {d.total_rides} rides | {d.rating > 0 ? `${d.rating} stars` : 'No ratings'}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => confirmAssign(d.driver_id)}
                      className={d.recommended ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
                      data-testid={`confirm-assign-${d.driver_id}`}>
                      Assign
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ====== BOOKING DETAIL PANEL ====== */}
      {selectedBooking && !showCRM && !showManifest && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={() => setSelectedBooking(null)}>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="booking-detail-panel">
            <div className="sticky top-0 bg-black text-white px-6 py-4 flex items-center justify-between">
              <div>
                <p className="font-mono text-xs text-gray-400">{selectedBooking.bookingId}</p>
                <h3 className="font-bold">{selectedBooking.customerInfo?.firstName} {selectedBooking.customerInfo?.lastName}</h3>
              </div>
              <button onClick={() => setSelectedBooking(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>

            <div className="p-6 space-y-5">
              {/* Trip Details */}
              <div>
                <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Trip Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Service</span><span className="font-semibold capitalize">{selectedBooking.serviceType?.replace(/-/g, ' ')}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-semibold">{selectedBooking.pickupDate ? new Date(selectedBooking.pickupDate).toLocaleDateString() : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Time</span><span className="font-semibold">{selectedBooking.pickupTime || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Hours</span><span className="font-semibold">{selectedBooking.hours || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-bold text-lg text-black">${selectedBooking.totalPrice}</span></div>
                </div>
              </div>

              {/* Locations */}
              <div>
                <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Locations</h4>
                <div className="space-y-2 text-sm">
                  <p><span className="text-gray-500">Pickup:</span> {selectedBooking.pickupLocation || '—'}</p>
                  <p><span className="text-gray-500">Drop-off:</span> {selectedBooking.dropoffLocation || '—'}</p>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Client Contact</h4>
                <div className="space-y-1 text-sm">
                  <p className="font-semibold">{selectedBooking.customerInfo?.firstName} {selectedBooking.customerInfo?.lastName}</p>
                  <a href={`tel:${selectedBooking.customerInfo?.phone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                    <Phone className="h-3 w-3" /> {selectedBooking.customerInfo?.phone}
                  </a>
                  <p className="text-gray-500">{selectedBooking.customerInfo?.email}</p>
                </div>
              </div>

              {/* Special Requests */}
              {selectedBooking.specialRequests && (
                <div>
                  <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-2">Special Requests</h4>
                  <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded-lg border border-yellow-200">{selectedBooking.specialRequests}</p>
                </div>
              )}

              {/* Driver Assignment */}
              <div>
                <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Driver</h4>
                {selectedBooking.dispatch?.driver_name ? (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="font-semibold text-sm">{selectedBooking.dispatch.driver_name}</p>
                    <p className="text-xs text-gray-500">{selectedBooking.dispatch.driver_phone}</p>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => openAssignModal(selectedBooking)} className="bg-blue-600 hover:bg-blue-700 w-full">
                    <User className="h-4 w-4 mr-1" /> Assign Driver
                  </Button>
                )}
              </div>

              {/* Dispatch Notes */}
              <div>
                <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Dispatch Notes</h4>
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {dispatchNotes.length === 0 && <p className="text-xs text-gray-400">No notes yet</p>}
                  {dispatchNotes.map(n => (
                    <div key={n.note_id} className="bg-gray-50 rounded p-2 text-xs">
                      <p className="text-gray-700">{n.text}</p>
                      <p className="text-gray-400 mt-1">{n.author} — {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="text-sm border-gray-200"
                    onKeyDown={e => e.key === 'Enter' && addNote()}
                    data-testid="dispatch-note-input"
                  />
                  <Button size="sm" onClick={addNote} className="bg-black hover:bg-gray-800" data-testid="add-note-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                <Button size="sm" variant="outline" onClick={() => {
                  setSelectedBooking(null);
                  loadCustomerProfile(selectedBooking.customerInfo?.email);
                  setShowCRM(true);
                  loadCustomers();
                }} className="text-xs">
                  <Users className="h-3 w-3 mr-1" /> View in CRM
                </Button>
                <Button size="sm" variant="outline" className="text-xs">
                  <FileText className="h-3 w-3 mr-1" /> Invoice
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== CRM PANEL ====== */}
      {showCRM && (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={() => { setShowCRM(false); setSelectedCustomer(null); }}>
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="crm-panel">
            <div className="sticky top-0 bg-black text-white px-6 py-4 flex items-center justify-between z-10">
              <h3 className="font-bold text-lg">Customer CRM</h3>
              <button onClick={() => { setShowCRM(false); setSelectedCustomer(null); }}><X className="h-5 w-5 text-gray-400" /></button>
            </div>

            <div className="p-6">
              {selectedCustomer ? (
                <div className="space-y-5">
                  <button onClick={() => setSelectedCustomer(null)} className="text-sm text-blue-600 hover:underline">&larr; Back to list</button>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold text-black">{selectedCustomer.first_name} {selectedCustomer.last_name}</h3>
                      <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                      <p className="text-sm text-gray-500">{selectedCustomer.phone}</p>
                    </div>
                    <Button size="sm" variant={selectedCustomer.profile?.is_vip ? 'default' : 'outline'}
                      onClick={() => toggleVIP(selectedCustomer.email, selectedCustomer.profile?.is_vip)}
                      className={selectedCustomer.profile?.is_vip ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                      data-testid="vip-toggle">
                      <Star className="h-4 w-4 mr-1" /> {selectedCustomer.profile?.is_vip ? 'VIP' : 'Mark VIP'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-black">{selectedCustomer.total_bookings}</p>
                      <p className="text-xs text-gray-500">Total Bookings</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">${(selectedCustomer.total_spent || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Total Spent</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{selectedCustomer.bookings?.length ? Math.round(selectedCustomer.total_spent / selectedCustomer.total_bookings) : 0}</p>
                      <p className="text-xs text-gray-500">Avg per Trip</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Booking History</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {(selectedCustomer.bookings || []).map((b, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg text-sm">
                          <div>
                            <span className="font-mono text-xs text-gray-400">{b.bookingId}</span>
                            <p className="text-xs text-gray-600 capitalize">{b.serviceType?.replace(/-/g, ' ')} — {b.pickupTime}</p>
                          </div>
                          <span className="font-bold">${b.totalPrice}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Communication Log */}
                  <div>
                    <h4 className="text-xs font-bold text-red-600 tracking-wider uppercase mb-3">Communication Log</h4>
                    {(selectedCustomer.communications || []).length === 0 && <p className="text-xs text-gray-400">No logged communications</p>}
                    {(selectedCustomer.communications || []).map((c, i) => (
                      <div key={i} className="bg-gray-50 rounded p-2 mb-2 text-xs">
                        <p className="font-semibold text-gray-700">{c.type?.toUpperCase()} — {c.subject || 'No subject'}</p>
                        <p className="text-gray-500">{c.content}</p>
                        <p className="text-gray-400 mt-1">{new Date(c.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search customers..."
                        value={customerSearch}
                        onChange={e => setCustomerSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && loadCustomers()}
                        className="pl-9 border-gray-200"
                        data-testid="crm-search"
                      />
                    </div>
                    <Button size="sm" onClick={loadCustomers} className="bg-black hover:bg-gray-800">Search</Button>
                  </div>

                  <div className="space-y-2">
                    {customers.map(c => (
                      <div key={c.email}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => loadCustomerProfile(c.email)}
                        data-testid={`customer-row-${c.email}`}
                      >
                        <div>
                          <p className="font-semibold text-sm text-black flex items-center gap-2">
                            {c.first_name} {c.last_name}
                            {c.is_vip && <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />}
                          </p>
                          <p className="text-xs text-gray-500">{c.email} {c.company && `| ${c.company}`}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm text-black">${(c.total_spent || 0).toLocaleString()}</p>
                          <p className="text-[10px] text-gray-400">{c.total_bookings} booking{c.total_bookings !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    ))}
                    {customers.length === 0 && (
                      <p className="text-center text-gray-400 text-sm py-8">No customers found. Customers appear after bookings are made.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== MANIFEST / RUN SHEET ====== */}
      {showManifest && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowManifest(false)}>
          <Card className="w-full max-w-4xl max-h-[85vh] overflow-y-auto bg-white p-6" onClick={e => e.stopPropagation()} data-testid="manifest-panel">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-black">Daily Run Sheet</h3>
                <p className="text-sm text-gray-500">{manifest?.day_of_week}, {manifest?.date} | {manifest?.total_rides || 0} rides | ${(manifest?.total_revenue || 0).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <Input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); }} className="w-auto border-gray-200" />
                <Button size="sm" onClick={loadManifest} className="bg-black hover:bg-gray-800">Load</Button>
                <button onClick={() => setShowManifest(false)}><X className="h-5 w-5 text-gray-400" /></button>
              </div>
            </div>

            {manifest && manifest.manifest?.length > 0 ? (
              <table className="w-full text-sm" data-testid="manifest-table">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="pb-2 pr-2">Time</th>
                    <th className="pb-2 pr-2">Client</th>
                    <th className="pb-2 pr-2">Pickup</th>
                    <th className="pb-2 pr-2">Drop-off</th>
                    <th className="pb-2 pr-2">Driver</th>
                    <th className="pb-2 pr-2">Service</th>
                    <th className="pb-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {manifest.manifest.map((m, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-3 pr-2 font-mono text-xs">{m.time || '—'}</td>
                      <td className="py-3 pr-2">
                        <p className="font-semibold">{m.client}</p>
                        <p className="text-xs text-gray-400">{m.phone}</p>
                      </td>
                      <td className="py-3 pr-2 text-xs text-gray-600 max-w-[150px] truncate">{m.pickup || '—'}</td>
                      <td className="py-3 pr-2 text-xs text-gray-600 max-w-[150px] truncate">{m.dropoff || '—'}</td>
                      <td className="py-3 pr-2">
                        <p className={`text-xs font-semibold ${m.driver === 'Unassigned' ? 'text-red-500' : 'text-black'}`}>{m.driver}</p>
                      </td>
                      <td className="py-3 pr-2 text-xs capitalize">{m.service?.replace(/-/g, ' ')}</td>
                      <td className="py-3 text-right font-bold">${m.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>No rides scheduled for this date</p>
              </div>
            )}

            {manifest && manifest.manifest?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <p className="text-sm text-gray-500">Total: {manifest.total_rides} rides</p>
                <p className="text-lg font-bold text-black">Revenue: ${(manifest.total_revenue || 0).toLocaleString()}</p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default DispatchBoard;
